import { service } from '@ember/service';
import { t } from 'ember-intl';
import Card from 'tio-reimagined/components/card';
import Chip from 'tio-reimagined/components/chip';
import type { ChipIntent } from 'tio-reimagined/components/chip';
import Component from '@glimmer/component';
import Text from 'tio-reimagined/components/text';
import { PhCheckCircle, PhWarning } from 'ember-phosphor-icons';
import type { IntlService } from 'ember-intl';
import type PslfFormModel from 'tio-common/models/pslf-form';
import { INACTIVE_STATUSES } from 'tio-common/models/pslf-form';

function getStatusChipIntent(status: string): ChipIntent {
  switch (status) {
    case 'submitted':
    case 're_submitted':
    case 'submit_pending':
    case 're_submit_pending':
      return 'default';
    case 'borrower_signed':
      return 'warning';
    case 'employer_signed':
    case 'awaiting_submit':
      return 'primary';
    default:
      return 'default';
  }
}

function getStatusLabel(status: string, intl: IntlService): string {
  switch (status) {
    case 'submitted':
    case 'submit_pending':
    case 're_submitted':
    case 're_submit_pending':
      return intl.t('pslf.reimagined.form_status.status.pending_verification');
    case 'borrower_signed':
      return intl.t('pslf.reimagined.form_status.status.awaiting_employer');
    case 'employer_signed':
    case 'awaiting_submit':
      return intl.t('pslf.reimagined.form_status.status.awaiting_submit');
    default:
      return intl.t('pslf.reimagined.form_status.status.processing');
  }
}

export interface FormStatusAlertsSignature {
  Args: {
    forms: PslfFormModel[];
  };
  Element: HTMLDivElement;
}

export default class PslfFormStatusAlerts extends Component<FormStatusAlertsSignature> {
  @service declare intl: IntlService;

  /**
   * Pick the single most relevant current employer form:
   * - Filter to currentEmployer only, exclude terminal statuses
   * - Sort by createdAt descending (newest first)
   * - Return the first one
   */
  get currentForm(): PslfFormModel | undefined {
    const candidates = this.args.forms
      .filter(
        (f) =>
          f.formType === 'currentEmployer' &&
          !(INACTIVE_STATUSES as readonly string[]).includes(f.status)
      )
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    return candidates[0];
  }

  get hasCurrentForm() {
    return !!this.currentForm;
  }

  /**
   * True when the displayed form is expiring (submitted 11+ months ago).
   */
  get isExpiring() {
    return !!this.currentForm?.isExpiring;
  }

  <template>
    <div ...attributes>
      {{#if this.hasCurrentForm}}
        {{#let this.currentForm as |form|}}
          {{#if form}}
            <Card
              @padding="none"
              @roundedXl={{true}}
              class="overflow-hidden"
              data-test-current-employer-alert
            >
              <div
                class={{if
                  this.isExpiring
                  "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800 p-4"
                  "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800 p-4"
                }}
              >
                <div class="flex flex-col gap-2">
                  <div class="flex items-center gap-2 flex-wrap">
                    {{#if this.isExpiring}}
                      <PhWarning
                        @size={{20}}
                        @weight="fill"
                        class="text-amber-600 dark:text-amber-400 shrink-0"
                      />
                    {{else}}
                      <PhCheckCircle
                        @size={{20}}
                        @weight="fill"
                        class="text-emerald-600 dark:text-emerald-400 shrink-0"
                      />
                    {{/if}}
                    <Text
                      @weight="semibold"
                      class={{if
                        this.isExpiring
                        "text-amber-900 dark:text-amber-100"
                        "text-emerald-900 dark:text-emerald-100"
                      }}
                    >
                      {{t "pslf.reimagined.form_status.current_employer.heading"}}
                    </Text>
                    <Chip @intent={{getStatusChipIntent form.status}} @size="sm">
                      {{getStatusLabel form.status this.intl}}
                    </Chip>
                  </div>
                  <Text
                    @size="sm"
                    class={{if
                      this.isExpiring
                      "text-amber-800 dark:text-amber-200"
                      "text-emerald-800 dark:text-emerald-200"
                    }}
                  >
                    {{#if this.isExpiring}}
                      {{t "pslf.reimagined.form_status.current_employer.expiring_description"}}
                    {{else}}
                      {{t "pslf.reimagined.form_status.current_employer.description"}}
                    {{/if}}
                  </Text>
                </div>
              </div>
            </Card>
          {{/if}}
        {{/let}}
      {{/if}}
    </div>
  </template>
}
